/**
 * Профайлер для RouteKernelLoader
 */

import { CopyOutlined, GlobalOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Input, Select, Space, Table, Tag, Typography, message } from 'antd'
import React, { useMemo, useState } from 'react'
import { Kernel } from '../../../Kernel'
import { DebugProfilerData, DebugProfilerInterface } from '../types'

const { Text } = Typography

// Компонент для відображення таблиці роутів з фільтрами
const RoutesTable: React.FC<{ routes: any[]; onRefresh?: () => void }> = ({ routes, onRefresh }) => {
    const [searchText, setSearchText] = useState('')
    const [groupFilter, setGroupFilter] = useState<string | undefined>(undefined)
    const [templateFilter, setTemplateFilter] = useState<string | undefined>(undefined)

    // Отримуємо унікальні значення для фільтрів
    const groups = useMemo(() => {
        const uniqueGroups = [...new Set(routes.map((route) => route.group || 'other'))]
        return uniqueGroups.sort()
    }, [routes])

    const templates = useMemo(() => {
        const uniqueTemplates = [...new Set(routes.map((route) => route.template).filter(Boolean))]
        return uniqueTemplates.sort()
    }, [routes])

    // Фільтрація даних
    const filteredRoutes = useMemo(() => {
        return routes.filter((route) => {
            // Пошук за підрядком у шляху, файлі та ключі роута
            const searchMatch =
                searchText === '' ||
                route.path.toLowerCase().includes(searchText.toLowerCase()) ||
                route.filePath.toLowerCase().includes(searchText.toLowerCase()) ||
                (route.key && route.key.toLowerCase().includes(searchText.toLowerCase()))

            // Фільтр по групі
            const groupMatch = groupFilter === undefined || (route.group || 'other') === groupFilter

            // Фільтр по шаблону
            const templateMatch = templateFilter === undefined || route.template === templateFilter

            return searchMatch && groupMatch && templateMatch
        })
    }, [routes, searchText, groupFilter, templateFilter])

    const getGroupColor = (group: string): string => {
        const colors: Record<string, string> = {
            ship: 'blue',
            container: 'green',
            resource: 'orange',
            other: 'default',
        }
        return colors[group] || 'default'
    }

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            message.success('Скопійовано!')
        })
    }

    const columns = [
        {
            title: 'Ключ роута',
            key: 'routeKey',
            render: (_: any, record: any) => {
                const routeKey = record.key
                if (!routeKey) {
                    return (
                        <Text type="secondary" style={{ fontSize: '13px', fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                            немає ключа
                        </Text>
                    )
                }
                return (
                    <Space size={4} style={{ whiteSpace: 'nowrap' }}>
                        <Text code style={{ fontSize: '13px', color: '#722ed1' }}>
                            {routeKey}
                        </Text>
                        <CopyOutlined
                            style={{ fontSize: '14px', cursor: 'pointer', color: '#1890ff' }}
                            onClick={() => handleCopy(routeKey)}
                        />
                    </Space>
                )
            },
        },
        {
            title: 'Шлях',
            dataIndex: 'path',
            key: 'path',
            render: (path: string) => (
                <Space size={4} style={{ whiteSpace: 'nowrap' }}>
                    <Text code style={{ fontSize: '13px' }}>
                        {path}
                    </Text>
                    <CopyOutlined
                        style={{ fontSize: '14px', cursor: 'pointer', color: '#1890ff' }}
                        onClick={() => handleCopy(path)}
                    />
                </Space>
            ),
        },
        {
            title: 'Файл',
            dataIndex: 'filePath',
            key: 'filePath',
            render: (filePath: string) => <Text style={{ fontSize: '13px' }}>{filePath}</Text>,
        },
        {
            title: 'Група',
            dataIndex: 'group',
            key: 'group',
            render: (group: string) => (
                <Tag color={getGroupColor(group)} style={{ fontSize: '12px' }}>
                    {group || 'other'}
                </Tag>
            ),
        },
        {
            title: 'Шаблон',
            dataIndex: 'template',
            key: 'template',
            render: (template: string) => (
                <Text type="secondary" style={{ fontSize: '13px' }}>
                    {template || '-'}
                </Text>
            ),
        },
    ]

    return (
        <div>
            <Space style={{ marginBottom: 16, width: '100%' }} wrap>
                <Input
                    placeholder="Пошук по ключу роута, шляху або файлу..."
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 280 }}
                />
                <Select
                    placeholder="Группа"
                    allowClear
                    value={groupFilter}
                    onChange={setGroupFilter}
                    style={{ width: 120 }}
                >
                    {groups.map((group) => (
                        <Select.Option key={group} value={group}>
                            {group}
                        </Select.Option>
                    ))}
                </Select>
                <Select
                    placeholder="Шаблон"
                    allowClear
                    value={templateFilter}
                    onChange={setTemplateFilter}
                    style={{ width: 150 }}
                >
                    {templates.map((template) => (
                        <Select.Option key={template} value={template}>
                            {template}
                        </Select.Option>
                    ))}
                </Select>
                {onRefresh && (
                    <Button icon={<ReloadOutlined />} onClick={onRefresh}>
                        Оновити
                    </Button>
                )}
            </Space>
            <Table
                columns={columns}
                dataSource={filteredRoutes.map((route, index) => ({
                    ...route,
                    tableKey: `route-${index}`, // Використовуємо tableKey для React, залишаємо route.key недоторканим
                }))}
                rowKey="tableKey"
                pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `Всього ${total} записів`,
                }}
                size="small"
                scroll={{ x: 'max-content' }}
            />
        </div>
    )
}

export class RouteLoaderProfiler implements DebugProfilerInterface {
    key = 'route-loader'
    name = 'Роути'
    icon = (<GlobalOutlined />)
    priority = 100

    isEnabled(): boolean {
        return true
    }

    async getData(onRefresh?: () => void): Promise<DebugProfilerData> {
        try {
            const kernel = Kernel.getInstance()
            const routeLoader = kernel.getLoader('route') as any

            if (!routeLoader) {
                return {
                    summary: {
                        count: 0,
                        status: 'error',
                        label: 'RouteLoader не знайдено',
                    },
                    details: {
                        stats: [
                            {
                                title: 'Статус',
                                value: 'RouteLoader не знайдено',
                                color: '#ff4d4f',
                            },
                        ],
                    },
                }
            }

            const routes = routeLoader.getAllRoutes ? routeLoader.getAllRoutes() : []

            if (!routes || routes.length === 0) {
                return {
                    summary: {
                        count: 0,
                        status: 'warning',
                        label: 'Нет данных',
                    },
                    details: {
                        stats: [
                            {
                                title: 'Статус',
                                value: 'Роути не завантажені',
                                color: '#faad14',
                            },
                        ],
                    },
                }
            }

            return {
                summary: {
                    count: routes.length,
                    status: routes.length > 0 ? 'success' : 'warning',
                    label: `${routes.length} роутів`,
                },
                details: {
                    custom: onRefresh ? (
                        <RoutesTable routes={routes} onRefresh={onRefresh} />
                    ) : (
                        <RoutesTable routes={routes} />
                    ),
                },
            }
        } catch (error) {
            console.error('Помилка отримання даних роутів:', error)

            return {
                summary: {
                    count: 0,
                    status: 'error',
                    label: 'Помилка',
                },
                details: {
                    stats: [
                        {
                            title: 'Помилка',
                            value: error instanceof Error ? error.message : 'Невідома помилка',
                            color: '#ff4d4f',
                        },
                    ],
                },
            }
        }
    }
}
